import { MemoryInfo } from '../types';

/**
 * 内存泄漏检测器
 */
export class MemoryLeakDetector {
  private memoryHistory: MemoryInfo[] = [];
  private maxHistoryLength = 50;
  private detectionInterval: number | null = null;
  private onLeakDetected?: (info: { current: MemoryInfo; trend: number }) => void;

  constructor(onLeakDetected?: (info: { current: MemoryInfo; trend: number }) => void) {
    this.onLeakDetected = onLeakDetected;
  }

  /**
   * 开始内存泄漏检测
   */
  start(intervalMs: number = 5000): void {
    if (this.detectionInterval) {
      this.stop();
    }

    this.detectionInterval = window.setInterval(() => {
      this.checkMemoryLeak();
    }, intervalMs);
  }

  /**
   * 停止内存泄漏检测
   */
  stop(): void {
    if (this.detectionInterval) {
      clearInterval(this.detectionInterval);
      this.detectionInterval = null;
    }
  }

  /**
   * 检查内存泄漏
   */
  private checkMemoryLeak(): void {
    const currentMemory = this.getMemoryInfo();
    if (!currentMemory) return;

    this.memoryHistory.push(currentMemory);

    // 保持历史记录长度
    if (this.memoryHistory.length > this.maxHistoryLength) {
      this.memoryHistory.shift();
    }

    // 计算内存增长趋势
    if (this.memoryHistory.length >= 10) {
      const trend = this.calculateMemoryTrend();
      
      // 如果内存持续增长且增长幅度超过阈值，认为可能存在内存泄漏
      if (trend > 0.1) { // 10%的增长阈值
        this.onLeakDetected?.({
          current: currentMemory,
          trend: trend
        });
      }
    }
  }

  /**
   * 计算内存增长趋势
   */
  private calculateMemoryTrend(): number {
    if (this.memoryHistory.length < 10) return 0;

    const recent = this.memoryHistory.slice(-10);
    const older = this.memoryHistory.slice(-20, -10);

    if (older.length === 0) return 0;

    const recentAvg = recent.reduce((sum, mem) => sum + mem.usedJSHeapSize, 0) / recent.length;
    const olderAvg = older.reduce((sum, mem) => sum + mem.usedJSHeapSize, 0) / older.length;

    return (recentAvg - olderAvg) / olderAvg;
  }

  /**
   * 获取当前内存信息
   */
  private getMemoryInfo(): MemoryInfo | null {
    if ('memory' in performance) {
      const memory = (performance as any).memory;
      return {
        usedJSHeapSize: memory.usedJSHeapSize,
        jsHeapSizeLimit: memory.jsHeapSizeLimit,
        totalJSHeapSize: memory.totalJSHeapSize
      };
    }
    return null;
  }

  /**
   * 获取内存历史记录
   */
  getMemoryHistory(): MemoryInfo[] {
    return [...this.memoryHistory];
  }

  /**
   * 清理历史记录
   */
  clearHistory(): void {
    this.memoryHistory = [];
  }
} 